import { API_CONFIG } from '../staticDataConfig';
import { PreferenciasUsuario, ConfiguracaoTema } from '../../types/preferencias';

declare global {
  interface Window {
    jQuery: any;
    AdminLTE?: any;
  }
}

interface ApiResponse<T = any> {
  sucesso: boolean;
  mensagem: string;
  dados?: T;
}

export class PreferenciasService {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_CONFIG.BASE_URL}/preferencias`;
  }

  /**
   * Buscar preferências do usuário logado
   */
  async buscarPreferencias(): Promise<ApiResponse<PreferenciasUsuario>> {
    try {
//       console.log('[PreferenciasService] Buscando preferências do usuário:', {
//         timestamp: new Date().toISOString(),
//         baseURL: this.baseURL
//       });

      const response = await fetch(`${this.baseURL}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const resultado = await response.json();

//       console.log('[PreferenciasService] Preferências recebidas:', {
//         sucesso: resultado.sucesso,
//         temDados: !!resultado.dados,
//         darkMode: resultado.dados?.dark_mode,
//         temaCor: resultado.dados?.tema_cor,
//         timestamp: new Date().toISOString()
//       });

      return resultado;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[PreferenciasService] Erro ao buscar preferências:', {
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: `Erro ao buscar preferências: ${errorMessage}`
      };
    }
  }

  /**
   * Atualizar preferências do usuário
   */
  async atualizarPreferencias(dados: Partial<PreferenciasUsuario>): Promise<ApiResponse<PreferenciasUsuario>> {
    try {
//       console.log('[PreferenciasService] Atualizando preferências:', {
//         campos: Object.keys(dados),
//         timestamp: new Date().toISOString()
//       });

      const response = await fetch(`${this.baseURL}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify(dados)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const resultado = await response.json();

//       console.log('[PreferenciasService] Preferências atualizadas:', {
//         sucesso: resultado.sucesso,
//         mensagem: resultado.mensagem,
//         timestamp: new Date().toISOString()
//       });

      return resultado;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[PreferenciasService] Erro ao atualizar preferências:', {
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: `Erro ao atualizar preferências: ${errorMessage}`
      };
    }
  }

  /**
   * Aplicar tema - obter classes CSS
   */
  async aplicarTema(): Promise<ApiResponse<{ classes: string[]; preferencias: PreferenciasUsuario }>> {
    try {
//       console.log('[PreferenciasService] Aplicando tema - gerando classes CSS:', {
//         timestamp: new Date().toISOString()
//       });

      const response = await fetch(`${this.baseURL}/aplicar-tema`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const resultado = await response.json();

//       console.log('[PreferenciasService] Classes CSS geradas:', {
//         sucesso: resultado.sucesso,
//         classes: resultado.dados?.classes,
//         timestamp: new Date().toISOString()
//       });

      return resultado;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[PreferenciasService] Erro ao aplicar tema:', {
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: `Erro ao aplicar tema: ${errorMessage}`
      };
    }
  }

  /**
   * Resetar preferências para o padrão
   */
  async resetarPreferencias(): Promise<ApiResponse<PreferenciasUsuario>> {
    try {
//       console.log('[PreferenciasService] Resetando preferências para o padrão:', {
//         timestamp: new Date().toISOString()
//       });

      const response = await fetch(`${this.baseURL}/reset`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const resultado = await response.json();

//       console.log('[PreferenciasService] Preferências resetadas:', {
//         sucesso: resultado.sucesso,
//         timestamp: new Date().toISOString()
//       });

      return resultado;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      console.error('[PreferenciasService] Erro ao resetar preferências:', {
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      return {
        sucesso: false,
        mensagem: `Erro ao resetar preferências: ${errorMessage}`
      };
    }
  }

  /**
   * Converter PreferenciasUsuario para ConfiguracaoTema (compatibilidade)
   */
  converterParaConfiguracaoTema(preferencias: PreferenciasUsuario): ConfiguracaoTema {
    return {
      // Campos atuais (compatibilidade)
      darkMode: preferencias.dark_mode,
      corPrimaria: preferencias.tema_cor,
      corSecundaria: 'secondary',
      tamanhoFonte: preferencias.tamanho_fonte,
      layoutCompacto: preferencias.densidade_interface === 'compacta',
      
      // Novos campos expandidos
      temaCor: preferencias.tema_cor,
      corSidebar: preferencias.cor_sidebar,
      corNavbar: preferencias.cor_navbar,
      corAccent: preferencias.cor_accent,
      
      layoutTipo: preferencias.layout_tipo,
      layoutFixo: preferencias.layout_fixo,
      navbarFixo: preferencias.navbar_fixo,
      footerFixo: preferencias.footer_fixo,
      sidebarRecolhido: preferencias.sidebar_recolhido,
      sidebarMini: preferencias.sidebar_mini,
      
      densidadeInterface: preferencias.densidade_interface,
      fonteFamilia: preferencias.fonte_familia,
      
      animacoesHabilitadas: preferencias.animacoes_habilitadas,
      sonsHabilitados: preferencias.sons_habilitados,
      notificacesDesktop: preferencias.notificacoes_desktop,
      modoAltoContraste: preferencias.modo_alto_contraste,
      
      cardsArredondados: preferencias.cards_arredondados,
      bordasSuaves: preferencias.bordas_suaves,
      sombrasHabilitadas: preferencias.sombras_habilitadas,
      gradientesHabilitados: preferencias.gradientes_habilitados,
      
      autoSalvar: preferencias.auto_salvar,
      confirmarAcoes: preferencias.confirmar_acoes,
      atalhosTeclado: preferencias.atalhos_teclado,
      navegacaoBreadcrumb: preferencias.navegacao_breadcrumb
    };
  }

  /**
   * Converter ConfiguracaoTema para PreferenciasUsuario (compatibilidade)
   */
  converterDeConfiguracaoTema(config: Partial<ConfiguracaoTema>): Partial<PreferenciasUsuario> {
    const dados: Partial<PreferenciasUsuario> = {};

    // Mapeamento de campos
    if (config.darkMode !== undefined) dados.dark_mode = config.darkMode;
    if (config.corPrimaria !== undefined) dados.tema_cor = config.corPrimaria as any;
    if (config.tamanhoFonte !== undefined) dados.tamanho_fonte = config.tamanhoFonte;
    if (config.layoutCompacto !== undefined) {
      dados.densidade_interface = config.layoutCompacto ? 'compacta' : 'normal';
    }

    // Novos campos
    if (config.temaCor !== undefined) dados.tema_cor = config.temaCor;
    if (config.corSidebar !== undefined) dados.cor_sidebar = config.corSidebar;
    if (config.corNavbar !== undefined) dados.cor_navbar = config.corNavbar;
    if (config.corAccent !== undefined) dados.cor_accent = config.corAccent;
    
    if (config.layoutTipo !== undefined) dados.layout_tipo = config.layoutTipo;
    if (config.layoutFixo !== undefined) dados.layout_fixo = config.layoutFixo;
    if (config.navbarFixo !== undefined) dados.navbar_fixo = config.navbarFixo;
    if (config.footerFixo !== undefined) dados.footer_fixo = config.footerFixo;
    if (config.sidebarRecolhido !== undefined) dados.sidebar_recolhido = config.sidebarRecolhido;
    if (config.sidebarMini !== undefined) dados.sidebar_mini = config.sidebarMini;
    
    if (config.densidadeInterface !== undefined) dados.densidade_interface = config.densidadeInterface;
    if (config.fonteFamilia !== undefined) dados.fonte_familia = config.fonteFamilia;
    
    if (config.animacoesHabilitadas !== undefined) dados.animacoes_habilitadas = config.animacoesHabilitadas;
    if (config.sonsHabilitados !== undefined) dados.sons_habilitados = config.sonsHabilitados;
    if (config.notificacesDesktop !== undefined) dados.notificacoes_desktop = config.notificacesDesktop;
    if (config.modoAltoContraste !== undefined) dados.modo_alto_contraste = config.modoAltoContraste;
    
    if (config.cardsArredondados !== undefined) dados.cards_arredondados = config.cardsArredondados;
    if (config.bordasSuaves !== undefined) dados.bordas_suaves = config.bordasSuaves;
    if (config.sombrasHabilitadas !== undefined) dados.sombras_habilitadas = config.sombrasHabilitadas;
    if (config.gradientesHabilitados !== undefined) dados.gradientes_habilitados = config.gradientesHabilitados;
    
    if (config.autoSalvar !== undefined) dados.auto_salvar = config.autoSalvar;
    if (config.confirmarAcoes !== undefined) dados.confirmar_acoes = config.confirmarAcoes;
    if (config.atalhosTeclado !== undefined) dados.atalhos_teclado = config.atalhosTeclado;
    if (config.navegacaoBreadcrumb !== undefined) dados.navegacao_breadcrumb = config.navegacaoBreadcrumb;

    return dados;
  }

  /**
   * Aplicar classes CSS no body do documento - SEM PISCAR
   */
  aplicarClassesCSS(classes: string[]): void {
    const body = document.body;
    
    // Lista completa de classes AdminLTE que podem ser aplicadas
    const classesAdminLTE = [
      'dark-mode', 'layout-boxed', 'layout-top-nav', 'layout-fixed',
      'layout-navbar-fixed', 'layout-footer-fixed', 'sidebar-collapse',
      'sidebar-mini', 'layout-sm', 'layout-lg'
    ];
    
    // Lista completa de classes de cores
    const coresAdminLTE = [
      'accent-primary', 'accent-secondary', 'accent-success', 'accent-info',
      'accent-warning', 'accent-danger', 'accent-indigo', 'accent-navy',
      'accent-purple', 'accent-fuchsia', 'accent-pink', 'accent-maroon',
      'accent-orange', 'accent-lime', 'accent-teal', 'accent-olive'
    ];

//     console.log('[PreferenciasService] Estado inicial - classes no body:', Array.from(body.classList));

    // ESTRATÉGIA INTELIGENTE: Aplicar/Remover apenas o que mudou
    const todasClassesControladas = [...classesAdminLTE, ...coresAdminLTE];
    const classesDesejadas = new Set(classes.filter(c => c && typeof c === 'string'));
    
//     console.log('[PreferenciasService] Classes desejadas:', Array.from(classesDesejadas));

    // Remover apenas classes que NÃO estão nas desejadas
    todasClassesControladas.forEach(classe => {
      const temAtualmente = body.classList.contains(classe);
      const deveEstar = classesDesejadas.has(classe);
      
      if (temAtualmente && !deveEstar) {
        body.classList.remove(classe);
//         console.log('[PreferenciasService] REMOVIDA classe desnecessária:', classe);
      }
    });

    // Adicionar apenas classes que ainda NÃO estão presentes
    classesDesejadas.forEach(classe => {
      if (!body.classList.contains(classe)) {
        body.classList.add(classe);
//         console.log('[PreferenciasService] ADICIONADA nova classe:', classe);
      } else {
//         console.log('[PreferenciasService] Classe já presente (mantida):', classe);
      }
    });

//     console.log('[PreferenciasService] Classes CSS aplicadas - ESTADO FINAL:', {
//       classesDesejadas: Array.from(classesDesejadas),
//       bodyClassesFinal: Array.from(body.classList),
//       darkModeAtivo: body.classList.contains('dark-mode'),
//       timestamp: new Date().toISOString()
//     });
  }

  /**
   * Carregar e aplicar preferências automaticamente
   */
  async carregarEAplicarPreferencias(): Promise<boolean> {
    try {
//       console.log('[PreferenciasService] Carregando e aplicando preferências automaticamente:', {
//         timestamp: new Date().toISOString()
//       });

      // Verificar se usuário está autenticado
      const token = sessionStorage.getItem('token');
      if (!token) {
//         console.log('[PreferenciasService] Usuário não autenticado - aplicando preferências padrão');
        this.aplicarPreferenciasPadrao();
        return false;
      }

      // Buscar preferências do usuário
      const resultado = await this.buscarPreferencias();
      
      if (resultado.sucesso && resultado.dados) {
        // Aplicar tema com as preferências do usuário
        this.aplicarTemaCompleto(resultado.dados);
        
        // Salvar no localStorage para persistência
        localStorage.setItem('preferencias_usuario', JSON.stringify(resultado.dados));
        localStorage.setItem('tema_carregado', 'true');
        
//         console.log('[PreferenciasService] Preferências carregadas e aplicadas com sucesso:', {
//           darkMode: resultado.dados.dark_mode,
//           temaCor: resultado.dados.tema_cor,
//           layoutFixo: resultado.dados.layout_fixo,
//           timestamp: new Date().toISOString()
//         });
        
        return true;
      } else {
//         console.warn('[PreferenciasService] Preferências não encontradas - aplicando padrão');
        this.aplicarPreferenciasPadrao();
        return false;
      }

    } catch (error) {
      console.error('[PreferenciasService] Erro ao carregar preferências:', error);
      this.aplicarPreferenciasPadrao();
      return false;
    }
  }

  /**
   * Aplicar tema completo baseado nas preferências - OTIMIZADO PARA NÃO PISCAR
   */
  aplicarTemaCompleto(preferencias: PreferenciasUsuario): void {
    try {
//       console.log('[PreferenciasService] Aplicando tema completo OTIMIZADO:', {
//         darkMode: preferencias.dark_mode,
//         temaCor: preferencias.tema_cor,
//         layoutTipo: preferencias.layout_tipo,
//         sidebarRecolhido: preferencias.sidebar_recolhido,
//         sidebarMini: preferencias.sidebar_mini,
//         timestamp: new Date().toISOString()
//       });

      const body = document.body;
      const classes: string[] = [];

      // Debug: mostrar estado atual do body antes da mudança
//       console.log('[PreferenciasService] Estado atual do body ANTES:', {
//         classList: Array.from(body.classList),
//         darkModeJaAtivo: body.classList.contains('dark-mode')
//       });

      // APLICAÇÃO IMEDIATA E DIRETA DO DARK MODE
      if (preferencias.dark_mode) {
        classes.push('dark-mode');
        // Aplicar imediatamente sem esperar
        if (!body.classList.contains('dark-mode')) {
          body.classList.add('dark-mode');
//           console.log('[PreferenciasService] DARK MODE ATIVADO IMEDIATAMENTE');
        }
      } else {
        // Remover imediatamente se não deve estar ativo
        if (body.classList.contains('dark-mode')) {
          body.classList.remove('dark-mode');
//           console.log('[PreferenciasService] DARK MODE DESATIVADO IMEDIATAMENTE');
        }
      }

      // Layout fixo
      if (preferencias.layout_fixo) {
        classes.push('layout-fixed');
      }

      // Navbar fixo
      if (preferencias.navbar_fixo) {
        classes.push('layout-navbar-fixed');
      }

      // Footer fixo
      if (preferencias.footer_fixo) {
        classes.push('layout-footer-fixed');
      }

      // Sidebar recolhido - OCULTO TEMPORARIAMENTE
      // if (preferencias.sidebar_recolhido && !preferencias.sidebar_mini) {
      //   classes.push('sidebar-collapse');
      // }

      // Sidebar mini - OCULTO TEMPORARIAMENTE
      // if (preferencias.sidebar_mini && !preferencias.sidebar_recolhido) {
      //   classes.push('sidebar-mini');
      // }

      // Layout tipo (boxed, top-nav, etc)
      if (preferencias.layout_tipo && preferencias.layout_tipo !== 'normal') {
        classes.push(`layout-${preferencias.layout_tipo}`);
      }

      // Cor de destaque/accent - aplicar como tema principal também
      if (preferencias.cor_accent && preferencias.cor_accent !== 'primary') {
        classes.push(`accent-${preferencias.cor_accent}`);
      }

//       console.log('[PreferenciasService] Classes que serão aplicadas (exceto dark-mode já aplicado):', classes);

      // Aplicar classes no body COM ESTRATÉGIA INTELIGENTE
      this.aplicarClassesCSS(classes);

      // Verificar se dark mode está realmente ativo após aplicação
      const darkModeAposAplicacao = body.classList.contains('dark-mode');
//       console.log('[PreferenciasService] VERIFICAÇÃO DARK MODE:', {
//         deveria: preferencias.dark_mode,
//         esta: darkModeAposAplicacao,
//         match: preferencias.dark_mode === darkModeAposAplicacao
//       });

      // Aplicar cor do tema principal com novos esquemas
      if (preferencias.tema_cor) {
        this.aplicarEsquemaCores(preferencias.tema_cor);
      }

      // Aplicar tamanho da fonte com estilos CSS dinâmicos
      if (preferencias.tamanho_fonte) {
        this.aplicarTamanhoFonte(preferencias.tamanho_fonte);
      }

      // Aplicar família da fonte com estilos CSS dinâmicos
      if (preferencias.fonte_familia) {
        this.aplicarFamiliaFonte(preferencias.fonte_familia);
      }

      // Aplicar densidade da interface
      if (preferencias.densidade_interface) {
        body.classList.remove('interface-compact', 'interface-normal', 'interface-spacious');
        body.classList.add(`interface-${preferencias.densidade_interface === 'compacta' ? 'compact' : preferencias.densidade_interface === 'espaçosa' ? 'spacious' : 'normal'}`);
      }

      // Debug: mostrar estado final do body
//       console.log('[PreferenciasService] Estado final do body APÓS aplicação:', {
//         classList: Array.from(body.classList),
//         darkModeAtivo: body.classList.contains('dark-mode'),
//         preferenciaDarkMode: preferencias.dark_mode,
//         sucesso: preferencias.dark_mode === body.classList.contains('dark-mode')
//       });

//       console.log('[PreferenciasService] ✅ Tema completo aplicado com SUCESSO:', {
//         classes,
//         bodyClasses: Array.from(body.classList),
//         darkModeConfirmed: body.classList.contains('dark-mode'),
//         timestamp: new Date().toISOString()
//       });

    } catch (error) {
      console.error('[PreferenciasService] ❌ Erro ao aplicar tema completo:', {
        error: error instanceof Error ? error.message : error,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Aplicar tamanho da fonte dinamicamente
   */
  aplicarTamanhoFonte(tamanho: string): void {
    try {
      // Remover estilo anterior
      const existingStyle = document.getElementById('dynamic-font-size');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      // Mapear tamanhos corretamente
      let pixelSize: string;
      
      switch (tamanho) {
        case 'pequeno':
          pixelSize = '14px';
          break;
        case 'medio':
          pixelSize = '16px';
          break;
        case 'grande':
          pixelSize = '18px';
          break;
        default:
          pixelSize = '16px';
      }
      
      // Criar CSS com máxima prioridade
      const style = document.createElement('style');
      style.id = 'dynamic-font-size';
      style.setAttribute('data-font-size', tamanho);
      
      // CSS com especificidade alta e !important
      style.textContent = `
        /* TAMANHO DE FONTE: ${tamanho.toUpperCase()} = ${pixelSize} */
        
        /* Base global */
        html, body {
          font-size: ${pixelSize} !important;
        }
        
        /* Elementos principais */
        .wrapper, .main-sidebar, .content-wrapper, .main-footer,
        .card, .card-body, .card-title, .card-text,
        .form-control, .form-label, .form-select,
        .btn, .nav-link, p, span, div, td, th, li,
        input, select, textarea {
          font-size: ${pixelSize} !important;
        }
        
        /* Navegação específica */
        .sidebar .nav-link, .navbar-nav .nav-link {
          font-size: ${pixelSize} !important;
        }
        
        /* Text helpers menores */
        .form-text, .text-muted, small {
          font-size: calc(${pixelSize} - 2px) !important;
        }
        
        /* Headers proporcionais */
        h1 { font-size: calc(${pixelSize} + 12px) !important; }
        h2 { font-size: calc(${pixelSize} + 10px) !important; }
        h3 { font-size: calc(${pixelSize} + 8px) !important; }
        h4 { font-size: calc(${pixelSize} + 6px) !important; }
        h5 { font-size: calc(${pixelSize} + 4px) !important; }
        h6 { font-size: calc(${pixelSize} + 2px) !important; }
      `;
      
      // Inserir no head
      document.head.appendChild(style);
      
//       console.log('[PreferenciasService] Tamanho da fonte aplicado:', tamanho, '->', pixelSize);
      
    } catch (error) {
      console.error('[PreferenciasService] Erro ao aplicar tamanho da fonte:', error);
    }
  }

  /**
   * Aplicar esquema de cores inspirador
   */
  aplicarEsquemaCores(esquema: string): void {
    try {
      const style = document.getElementById('color-scheme-style') || document.createElement('style');
      style.id = 'color-scheme-style';
      
      let cssVars = '';
      
      switch (esquema) {
        case 'primary': // Oceano Profundo
          cssVars = `
            :root {
              --bs-primary: #007bff;
              --bs-primary-rgb: 0, 123, 255;
              --bs-success: #28a745;
              --bs-info: #17a2b8;
              --bs-warning: #ffc107;
              --bs-danger: #dc3545;
              --theme-gradient: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
            }
          `;
          break;
        case 'emerald': // Floresta Serena
          cssVars = `
            :root {
              --bs-primary: #10b981;
              --bs-primary-rgb: 16, 185, 129;
              --bs-success: #059669;
              --bs-info: #0891b2;
              --bs-warning: #f59e0b;
              --bs-danger: #ef4444;
              --theme-gradient: linear-gradient(135deg, #10b981 0%, #059669 100%);
            }
          `;
          break;
        case 'sunset': // Pôr do Sol
          cssVars = `
            :root {
              --bs-primary: #f97316;
              --bs-primary-rgb: 249, 115, 22;
              --bs-success: #22c55e;
              --bs-info: #3b82f6;
              --bs-warning: #eab308;
              --bs-danger: #dc2626;
              --theme-gradient: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
            }
          `;
          break;
        case 'royal': // Realeza
          cssVars = `
            :root {
              --bs-primary: #8b5cf6;
              --bs-primary-rgb: 139, 92, 246;
              --bs-success: #10b981;
              --bs-info: #3b82f6;
              --bs-warning: #f59e0b;
              --bs-danger: #ef4444;
              --theme-gradient: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
            }
          `;
          break;
        case 'crimson': // Paixão Ardente
          cssVars = `
            :root {
              --bs-primary: #dc2626;
              --bs-primary-rgb: 220, 38, 38;
              --bs-success: #10b981;
              --bs-info: #3b82f6;
              --bs-warning: #f59e0b;
              --bs-danger: #b91c1c;
              --theme-gradient: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            }
          `;
          break;
        default:
          cssVars = `
            :root {
              --bs-primary: #007bff;
              --bs-primary-rgb: 0, 123, 255;
              --theme-gradient: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
            }
          `;
      }
      
      // Adicionar estilos adicionais para botões e elementos (SEM alterar cores de texto)
      style.textContent = `
        ${cssVars}
        
        /* Aplicar cores APENAS nos botões e elementos primários */
        .btn-primary {
          background-color: var(--bs-primary) !important;
          border-color: var(--bs-primary) !important;
          color: #fff !important; /* Manter texto branco nos botões */
        }
        
        .btn-primary:hover, .btn-primary:focus {
          background-color: color-mix(in srgb, var(--bs-primary) 85%, black) !important;
          border-color: color-mix(in srgb, var(--bs-primary) 85%, black) !important;
          color: #fff !important; /* Manter texto branco nos botões */
        }
        
        /* Cards primários */
        .card-primary .card-header {
          background-color: var(--bs-primary) !important;
          border-color: var(--bs-primary) !important;
          color: #fff !important; /* Texto branco no header */
        }
        
        .card-primary.card-outline .card-header {
          background-color: transparent !important;
          border-color: var(--bs-primary) !important;
          color: var(--bs-primary) !important;
        }
        
        /* Elementos de formulário com foco */
        .form-control:focus, .form-select:focus {
          border-color: var(--bs-primary) !important;
          box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25) !important;
        }
        
        /* Barras de progresso */
        .progress-bar, .bg-primary {
          background-color: var(--bs-primary) !important;
        }
        
        /* Links primários (apenas em contextos específicos) */
        .btn-link.text-primary {
          color: var(--bs-primary) !important;
        }
        
        .btn-link.text-primary:hover {
          color: color-mix(in srgb, var(--bs-primary) 85%, black) !important;
        }
        
        /* Navegação ativa (sem alterar cores de texto padrão) */
        .main-sidebar .nav-link.active {
          background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
          /* NÃO alterar color - manter padrão do AdminLTE */
        }
        
        /* Elementos com gradiente */
        .bg-gradient-primary {
          background: var(--theme-gradient) !important;
        }
        
        /* Badges e elementos pequenos */
        .badge-primary {
          background-color: var(--bs-primary) !important;
          color: #fff !important;
        }
        
        /* MANTER cores de texto padrão em todos os outros elementos */
        /* Não alterar .nav-link, .sidebar, .brand-text, etc. */
      `;
      
      if (!document.head.contains(style)) {
        document.head.appendChild(style);
      }
      
//       console.log('[PreferenciasService] Esquema de cores aplicado (mantendo textos padrão):', esquema);
    } catch (error) {
      console.error('[PreferenciasService] Erro ao aplicar esquema de cores:', error);
    }
  }
  aplicarFamiliaFonte(familia: string): void {
    try {
      const style = document.getElementById('font-family-style') || document.createElement('style');
      style.id = 'font-family-style';
      
      // Importar a fonte se necessário
      let importStatement = '';
      if (familia === 'Inter') {
        importStatement = '@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");';
      } else if (familia === 'Roboto') {
        importStatement = '@import url("https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap");';
      } else if (familia === 'Open Sans') {
        importStatement = '@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap");';
      } else if (familia === 'Lato') {
        importStatement = '@import url("https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700&display=swap");';
      } else if (familia === 'Nunito') {
        importStatement = '@import url("https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700&display=swap");';
      } else if (familia === 'Poppins') {
        importStatement = '@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap");';
      } else if (familia === 'Montserrat') {
        importStatement = '@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap");';
      }
      
      style.textContent = `
        ${importStatement}
        body, .wrapper, .main-sidebar, .content-wrapper, .main-footer,
        .card, .card-body, .form-control, .btn, .nav-link,
        h1, h2, h3, h4, h5, h6, p, span, div, input, select, textarea,
        .sidebar .nav-link, .brand-text {
          font-family: "${familia}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
        }
      `;
      
      if (!document.head.contains(style)) {
        document.head.appendChild(style);
      }
      
//       console.log('[PreferenciasService] Família da fonte aplicada:', familia);
    } catch (error) {
      console.error('[PreferenciasService] Erro ao aplicar família da fonte:', error);
    }
  }

  /**
   * Aplicar preferências padrão
   */
  aplicarPreferenciasPadrao(): void {
    try {
//       console.log('[PreferenciasService] Aplicando preferências padrão');

      const preferenciasDefault: Partial<PreferenciasUsuario> = {
        dark_mode: false,
        tema_cor: 'primary',
        cor_sidebar: 'dark-primary',
        cor_navbar: 'white',
        cor_accent: 'primary',
        layout_tipo: 'normal',
        layout_fixo: true,
        navbar_fixo: true,
        footer_fixo: false,
        sidebar_recolhido: false,
        sidebar_mini: false,
        tamanho_fonte: 'medio',
        densidade_interface: 'normal',
        fonte_familia: 'Source Sans Pro',
        animacoes_habilitadas: true
      };

      this.aplicarTemaCompleto(preferenciasDefault as PreferenciasUsuario);

    } catch (error) {
      console.error('[PreferenciasService] Erro ao aplicar preferências padrão:', error);
    }
  }

  /**
   * Verificar se preferências estão carregadas do localStorage
   */
  obterPreferenciasDoLocalStorage(): PreferenciasUsuario | null {
    try {
      const preferenciasString = localStorage.getItem('preferencias_usuario');
      if (preferenciasString) {
        return JSON.parse(preferenciasString);
      }
      return null;
    } catch (error) {
      console.error('[PreferenciasService] Erro ao obter preferências do localStorage:', error);
      return null;
    }
  }

  /**
   * Aplicar preferências do localStorage (para carregamento rápido)
   */
  aplicarPreferenciasDoLocalStorage(): boolean {
    try {
      const preferencias = this.obterPreferenciasDoLocalStorage();
      if (preferencias) {
//         console.log('[PreferenciasService] Aplicando preferências do localStorage (carregamento rápido)');
        this.aplicarTemaCompleto(preferencias);
        return true;
      }
      return false;
    } catch (error) {
      console.error('[PreferenciasService] Erro ao aplicar preferências do localStorage:', error);
      return false;
    }
  }
}

// Instância singleton do serviço
export const preferenciasService = new PreferenciasService();
